import {
  CONNECTION_PIPELINE_STAGES,
  CONTACT_GROUPS,
  RELATIONSHIP_LEVELS,
  RELATIONSHIP_MOTIONS,
} from "../root/crmVocabulary.generated";
import type { SwipeFilter, SwipeRefinement, SwipeScenario } from "./types";

const MS_PER_DAY = 86_400_000;

const choicesFrom = (terms: readonly { value: string; label: string }[]) =>
  terms.map((term) => ({ value: term.value, label: term.label }));

/**
 * The narrowings a scenario can offer.
 *
 * Deliberately generic and few: these compose with any scenario's base filter,
 * so "ICP fit, ecosystem contacts only, untouched for a year" needs no new
 * scenario and no deploy — it is the ICP scenario with two selects set, and
 * the selection lives in the URL so it is a link.
 */
export const SWIPE_REFINEMENTS: Record<string, SwipeRefinement> = {
  motion: {
    id: "motion",
    label: "Motion",
    choices: choicesFrom(RELATIONSHIP_MOTIONS),
    toFilter: (value) => ({ "relationship_motion@eq": value }),
  },
  stage: {
    id: "stage",
    label: "Stage",
    choices: choicesFrom(CONNECTION_PIPELINE_STAGES),
    toFilter: (value) => ({ "connection_pipeline_stage@eq": value }),
  },
  level: {
    id: "level",
    label: "Level",
    choices: choicesFrom(RELATIONSHIP_LEVELS),
    toFilter: (value) => ({ "status@eq": value }),
  },
  group: {
    id: "group",
    label: "Group",
    choices: choicesFrom(CONTACT_GROUPS),
    toFilter: (value) => ({ "group@eq": value }),
  },
  staleness: {
    id: "staleness",
    label: "Quiet for",
    choices: [
      { value: "30", label: "30+ days" },
      { value: "90", label: "90+ days" },
      { value: "180", label: "180+ days" },
      { value: "365", label: "a year+" },
    ],
    // `@lt` excludes contacts with no recorded touch at all. That is the point:
    // "quiet for a year" is a claim about a relationship that existed, and a
    // contact you have never spoken to has not gone quiet.
    toFilter: (value, now) => ({
      "last_meaningful_interaction_at@lt": new Date(
        now.getTime() - Number(value) * MS_PER_DAY,
      ).toISOString(),
    }),
  },
  reachable: {
    id: "reachable",
    label: "Reachable",
    choices: [{ value: "linkedin", label: "Has a LinkedIn URL" }],
    toFilter: () => ({ "linkedin_url@not.is": null }),
  },
};

/** Selections keyed by refinement id. An absent or empty value means "any". */
export type SwipeRefinementSelection = Record<string, string | undefined>;

/**
 * The refinements a scenario offers, resolved and in the scenario's order.
 * An unknown id is dropped rather than thrown on, so a stale scenario
 * definition degrades to fewer selects instead of a blank page.
 */
export const refinementsFor = (scenario: SwipeScenario): SwipeRefinement[] =>
  (scenario.refinements ?? [])
    .map((id) => SWIPE_REFINEMENTS[id])
    .filter((refinement): refinement is SwipeRefinement => Boolean(refinement));

/**
 * Base filter AND the active refinements.
 *
 * A refinement never replaces a key the scenario set: where both constrain the
 * same column they use different operators, so PostgREST ANDs them and the
 * scenario's own constraint survives. Silently widening a scenario's queue
 * from a dropdown would be the one genuinely dangerous thing this could do.
 */
export const applyRefinements = (
  base: SwipeFilter,
  scenario: SwipeScenario,
  selection: SwipeRefinementSelection,
  now: Date,
): SwipeFilter => {
  const filter: SwipeFilter = { ...base };
  for (const refinement of refinementsFor(scenario)) {
    const value = selection[refinement.id];
    if (!value) continue;
    for (const [key, fragment] of Object.entries(
      refinement.toFilter(value, now),
    )) {
      if (key in filter) continue;
      filter[key] = fragment;
    }
  }
  return filter;
};

/** How many refinements are active, for the "N active" badge on the bar. */
export const countActive = (
  scenario: SwipeScenario,
  selection: SwipeRefinementSelection,
): number =>
  refinementsFor(scenario).filter((refinement) => selection[refinement.id])
    .length;
